import React, { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from './AuthContext'

interface ProtectedRouteProps {
  children: React.ReactNode
  requireRole?: 'admin' | 'editor' | 'viewer'
  requireOrg?: boolean
  /** viewerロールでもアクセス可能なルート（既定: false = viewerは閲覧者ホームへ） */
  allowViewer?: boolean
}

export function ProtectedRoute({
  children,
  requireRole,
  requireOrg = true,
  allowViewer = false
}: ProtectedRouteProps) {
  const { user, loading, refreshUser } = useAuth()
  const location = useLocation()
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [hasRefreshed, setHasRefreshed] = useState(false)

  // 組織がない場合、一度だけトークンリフレッシュを試みる
  useEffect(() => {
    const tryRefresh = async () => {
      if (!loading && user && requireOrg && !user.orgId && !hasRefreshed && !isRefreshing) {
        console.log('⚠️ orgIdが見つかりません。トークンをリフレッシュします...')
        setIsRefreshing(true)
        await refreshUser()
        setHasRefreshed(true)
        setIsRefreshing(false)
      }
    }
    tryRefresh()
  }, [user, loading, requireOrg, hasRefreshed, isRefreshing, refreshUser])

  if (loading || isRefreshing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  if (!user) {
    // ログイン後に元のページへ戻れるよう、現在のURLを引き継ぐ
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
  }

  if (requireOrg && !user.orgId) {
    // トークンリフレッシュ後もorgIdがない場合は組織作成ページへ
    if (hasRefreshed) {
      console.log('⚠️ トークンリフレッシュ後もorgIdが見つかりません。組織作成ページへリダイレクトします')
      return <Navigate to="/create-organization" replace />
    }
    // まだリフレッシュしていない場合はローディング表示
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  // 閲覧専用（viewer）ユーザーは通常アプリを開かず、閲覧者ホーム/個別議事録のみ
  if (!allowViewer && user.role === 'viewer') {
    return <Navigate to="/viewer" replace />
  }

  if (requireRole && user.role !== requireRole && user.role !== 'admin') {
    const roleNames = {
      admin: '管理者',
      editor: '編集者',
      viewer: '閲覧者'
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full space-y-8 text-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">権限が不足しています</h2>
            <p className="mt-2 text-gray-600">
              この機能には{roleNames[requireRole]}権限が必要です
            </p>
          </div>
        </div>
      </div>
    )
  }

  return <>{children}</>
}